import { useEnum } from "@/api/services/global/enum";
import { IconOutlineInfo } from "@/assets/icons";
import { FieldGrid } from "@/features/company/components/FieldGrid";
import { INVOICE_DATE_OFFSET_TOOLTIP, PAYMENT_DUE_OFFSET_TOOLTIP } from "@/features/company/constants/sections";
import { TCompanyForm } from "@/features/company/types/company-form";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { RadioGroupNew } from "@/modules/irohana-components/elements/RadioGroupNew";
import { SelectNew } from "@/modules/irohana-components/elements/SelectNew";
import { text } from "@/theme/colors";
import { InputAdornment, Typography } from "@mui/material";
import { FC } from "react";
import { useFormContext } from "react-hook-form";

/** Day-of-month suffix shown inside 請求日 / 支払期日. */
const DayAdornment = (
  <InputAdornment position="end">
    <Typography variant="cap12" color={text.secondary}>
      日
    </Typography>
  </InputAdornment>
);

const TooltipContent: FC<{ children: string }> = ({ children }) => (
  <Typography variant="cap12" whiteSpace="pre-line">
    {children}
  </Typography>
);

/** 請求タイミング設定 section: invoice / payment days, their month offsets, and the four 扱い rules. */
export const BillingTimingSection: FC = () => {
  const { control } = useFormContext<TCompanyForm>();

  // 請求日 / 支払期日 の月オフセットは同じサーバー enum を共有するので、取得は1回だけ。
  const { data: monthOffsetOptions, isLoading: isLoadingMonthOffset } = useEnum("EBillingMonthOffset");
  // 入社月 / 退職月 と 一時帰国 / 休職 は別 enum（後者に「請求する」は無い）。
  const { data: monthlyHandlingOptions } = useEnum("EMonthlyBillingHandling");
  const { data: absenceHandlingOptions } = useEnum("EAbsenceBillingHandling");

  return (
    <FieldGrid>
      <FormField<TCompanyForm> name="invoiceDay" control={control} fullWidth>
        <Input fullWidth clearable={false} transformToHalfWidth endAdornment={DayAdornment} />
      </FormField>
      <FormField<TCompanyForm> name="paymentDueDay" control={control} fullWidth>
        <Input fullWidth clearable={false} transformToHalfWidth endAdornment={DayAdornment} />
      </FormField>

      <FormField<TCompanyForm>
        name="invoiceDateMonthOffset"
        control={control}
        fullWidth
        helper={INVOICE_DATE_OFFSET_TOOLTIP}
        helperContent={<TooltipContent>{INVOICE_DATE_OFFSET_TOOLTIP}</TooltipContent>}
        helperIcon={<IconOutlineInfo />}
      >
        <SelectNew fullWidth options={monthOffsetOptions} loading={isLoadingMonthOffset} placeholder="選択内容" />
      </FormField>
      <FormField<TCompanyForm>
        name="paymentDueMonthOffset"
        control={control}
        fullWidth
        helper={PAYMENT_DUE_OFFSET_TOOLTIP}
        helperContent={<TooltipContent>{PAYMENT_DUE_OFFSET_TOOLTIP}</TooltipContent>}
        helperIcon={<IconOutlineInfo />}
      >
        <SelectNew fullWidth options={monthOffsetOptions} loading={isLoadingMonthOffset} placeholder="選択内容" />
      </FormField>

      <FormField<TCompanyForm> name="joinMonthHandling" control={control} fullWidth>
        <RadioGroupNew options={monthlyHandlingOptions} />
      </FormField>
      <FormField<TCompanyForm> name="leaveMonthHandling" control={control} fullWidth>
        <RadioGroupNew options={monthlyHandlingOptions} />
      </FormField>

      <FormField<TCompanyForm> name="temporaryReturnHandling" control={control} fullWidth>
        <RadioGroupNew options={absenceHandlingOptions} />
      </FormField>
      <FormField<TCompanyForm> name="leaveOfAbsenceHandling" control={control} fullWidth>
        <RadioGroupNew options={absenceHandlingOptions} />
      </FormField>
    </FieldGrid>
  );
};
